import React, { useMemo, useState } from "react";
import {
  Search, Filter, X, Download, Sparkles, Calendar,
} from "lucide-react";
import {
  EVENTS, EVENT_TYPES, EVENT_SOURCES, formatEventType, formatSource,
} from "@/data/mockData";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import DataTable from "@/components/DataTable";
import { cn } from "@/lib/utils";

const SOURCE_COLORS = {
  web: "text-sky-500 border-sky-500/30 bg-sky-500/10",
  mobile_app: "text-primary border-primary/30 bg-primary/10",
  branch_pos: "text-amber-500 border-amber-500/30 bg-amber-500/10",
  atm_kiosk: "text-fuchsia-500 border-fuchsia-500/30 bg-fuchsia-500/10",
  core_banking_erp: "text-emerald-500 border-emerald-500/30 bg-emerald-500/10",
  call_center: "text-orange-500 border-orange-500/30 bg-orange-500/10",
  partner_api: "text-slate-500 border-slate-500/30 bg-slate-500/10",
};

export default function EventsPage() {
  // Draft = form state (user edits), applied = actually used to filter
  const [draft, setDraft] = useState({ type: "all", source: "all", from: "", to: "" });
  const [applied, setApplied] = useState({ type: "all", source: "all", from: "", to: "" });
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => {
    return EVENTS.filter((e) => {
      if (applied.type !== "all" && e.event_type !== applied.type) return false;
      if (applied.source !== "all" && e.event_source !== applied.source) return false;
      if (applied.from && new Date(e.event_datetime) < new Date(applied.from)) return false;
      if (applied.to && new Date(e.event_datetime) > new Date(applied.to)) return false;
      return true;
    });
  }, [applied]);

  const applySearch = () => setApplied(draft);
  const resetFilters = () => {
    const empty = { type: "all", source: "all", from: "", to: "" };
    setDraft(empty); setApplied(empty);
  };

  const hasActive = applied.type !== "all" || applied.source !== "all" || applied.from || applied.to;

  const columns = useMemo(() => [
    {
      key: "event_id",
      header: "Event ID",
      className: "w-[180px] font-mono text-xs",
      render: (r) => <span className="font-mono text-xs">{r.event_id}</span>,
    },
    {
      key: "event_type",
      header: "Type",
      render: (r) => <span className="font-medium">{formatEventType(r.event_type)}</span>,
    },
    {
      key: "event_source",
      header: "Source",
      render: (r) => (
        <Badge variant="outline" className={cn("font-normal border", SOURCE_COLORS[r.event_source])}>
          {formatSource(r.event_source)}
        </Badge>
      ),
    },
    {
      key: "customer_code",
      header: "Customer",
      render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.customer_code}</span>,
    },
    {
      key: "event_datetime",
      header: "Timestamp",
      render: (r) => (
        <span className="text-xs text-muted-foreground">{new Date(r.event_datetime).toLocaleString()}</span>
      ),
    },
  ], []);

  return (
    <div className="p-6 lg:p-10 space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps mb-2">Ingested · {EVENTS.length.toLocaleString()} total</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Event Logs</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-xl">
            Browse and inspect every event received by the OptiKurve gateway.
            Click any row to view the raw payload.
          </p>
        </div>
        <Button variant="outline" data-testid="events-export" className="gap-2">
          <Download className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-5 border-border" data-testid="events-filters">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-primary" strokeWidth={1.5} />
          <div className="label-caps">Filters</div>
          {hasActive && (
            <button
              onClick={resetFilters}
              data-testid="events-filters-reset"
              className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <X className="w-3 h-3" /> reset
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* From */}
          <div className="space-y-1.5">
            <Label className="label-caps flex items-center gap-1.5"><Calendar className="w-3 h-3" /> From</Label>
            <Input
              data-testid="events-date-from"
              type="datetime-local"
              value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              className="h-10 text-xs"
            />
          </div>
          {/* To */}
          <div className="space-y-1.5">
            <Label className="label-caps flex items-center gap-1.5"><Calendar className="w-3 h-3" /> To</Label>
            <Input
              data-testid="events-date-to"
              type="datetime-local"
              value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              className="h-10 text-xs"
            />
          </div>
          {/* Type */}
          <div className="space-y-1.5">
            <Label className="label-caps">Event type</Label>
            <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
              <SelectTrigger data-testid="events-type-filter" className="h-10"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {EVENT_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{formatEventType(t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Source */}
          <div className="space-y-1.5">
            <Label className="label-caps">Source</Label>
            <Select value={draft.source} onValueChange={(v) => setDraft({ ...draft, source: v })}>
              <SelectTrigger data-testid="events-source-filter" className="h-10"><SelectValue placeholder="All sources" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                {EVENT_SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>{formatSource(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Search button */}
          <div className="space-y-1.5">
            <Label className="label-caps opacity-0 select-none">Apply</Label>
            <Button
              data-testid="events-search-button"
              onClick={applySearch}
              className="h-10 w-full bg-primary hover:bg-[#0B2B4E] gap-2"
            >
              <Search className="w-4 h-4" /> Search
            </Button>
          </div>
        </div>

        <div className="mt-4 text-xs text-muted-foreground flex items-center justify-between">
          <span>Showing <span className="text-foreground font-medium">{filtered.length}</span> of {EVENTS.length} events</span>
          <span className="font-mono">avg latency 38ms</span>
        </div>
      </Card>

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={filtered}
        onRowClick={setSelected}
        searchable={["event_id", "customer_code", "event_type", "event_source"]}
        testIdPrefix="events-table"
        initialPageSize={10}
      />

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent side="right" className="sm:max-w-lg w-full overflow-auto" data-testid="event-detail-sheet">
          {selected && (
            <>
              <SheetHeader>
                <div className="label-caps mb-2">Event detail</div>
                <SheetTitle className="font-display text-2xl">
                  {formatEventType(selected.event_type)}
                </SheetTitle>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <DetailRow label="Event ID" value={<span className="font-mono">{selected.event_id}</span>} />
                <DetailRow label="Customer" value={<span className="font-mono">{selected.customer_code}</span>} />
                <DetailRow label="Source" value={
                  <Badge variant="outline" className={cn("font-normal border", SOURCE_COLORS[selected.event_source])}>
                    {formatSource(selected.event_source)}
                  </Badge>
                } />
                <DetailRow label="Timestamp" value={new Date(selected.event_datetime).toLocaleString()} />

                <div>
                  <div className="label-caps mb-2">Payload</div>
                  <pre className="bg-secondary rounded-md p-4 border border-border font-mono text-xs overflow-auto max-h-80 leading-relaxed">
{JSON.stringify(selected.event_payload, null, 2)}
                  </pre>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="label-caps mb-2 flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-primary" /> AI context
                  </div>
                  <div className="text-sm text-muted-foreground leading-relaxed">
                    This event matches pattern <span className="text-foreground font-medium">&quot;{formatEventType(selected.event_type)}&quot;</span> observed
                    {" "}{Math.floor((selected.event_id.charCodeAt(4) * 3) % 200 + 30)} times in the last 24h from source {formatSource(selected.event_source)}.
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const DetailRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4 border-b border-border pb-3">
    <div className="label-caps">{label}</div>
    <div className="text-sm">{value}</div>
  </div>
);
